import { fastlane } from '@App/Socket'
import { meta } from '@App/Meta'
import Vue from 'vue'
import { sortBy, sortByDesc } from '@Shared/Functions'
import axios from 'axios'
import { formatarISO, utcParaLocal } from '@Shared/Date'
import { visitarPágina } from '@App/Reflinks'
import { aguardarPerfil, contactProfile, userProfile } from './Perfil'
import { asset } from '@App/GlobalHelpers'
import { toast } from '@App/Toast'
import * as Form from '@App/Utils/Form'
import {
    buildChatStore,
    findRoomById,
    canaisDoChat,
    canaisDoEmail,
    canaisDoTeams,
    roomTabName,
    markRoomEventsAsRead,
} from '@Shared/Chat/Store'
import { cockpitHelpdeskFilters, getCoreSpace } from './Settings'
import { defaultCustomFilters, roomPassAgainstFilters } from './Chat/Inbox'

const chatStore = buildChatStore({ fastlane, aguardarPerfil })
const MAX_ROOMS_CACHE_ROTATION = 31

let serializedEmailsTabs = []
try {
    serializedEmailsTabs = JSON.parse(localStorage.serializedEmailsTabs)
} catch (err) {}

export default {
    namespaced: true,
    state: {
        ...chatStore.state,

        /**
         * Sala do assistante pessoal. Cada usuário do Teclia tem uma sala de alertas.
         */
        personalAssistantRoom: null,

        /**
         * Qual aba do módulo chat o usuário está selecionado? Pode ser 'teams', 'chat' ou 'email'.
         */
        abaSelecionada: 'teams',

        /**
         * IDs das salas que estão na rotação de cache dos atendimentos do chat (chat e teams).
         */
        roomsIdsCacheRotation: [],

        /**
         * Pesquisa por atendimentos pelo cockpit, manipulada pelo componente `Search` no canto superior esquerdo.
         */
        appliedFilters: {
            searching: false,
            query: '',
            conditions: [],
            result: null,
        },

        /**
         * Ids das salas que estão selecionadas para aplicar uma ação em massa.
         */
        selectedRoomsForBulkAction: [],

        /**
         * Qual filtro do helpdesk está selecionado. Pode ser um dos filtros padrões 'live', 'unread', ou filtros
         * personalizados pelo ID.
         */
        helpdeskFilter: null,

        /**
         * Controla se os filtros do helpdesk.
         */
        helpdeskFiltersVisible: false,

        /**
         * Filtros customziados aplicados pelo usuário na página da inbox.
         */
        helpdeskCustomFilters: defaultCustomFilters(),

        /**
         * Registros das salas baseado no filtro selecionado pelo usuário. Essa lista não é atualizada em tempo real.
         */
        helpdeskPaginatedRooms: null,

        /**
         * Abas dos atendimentos do canal email que o usuário pode ter aberto em paralelo.
         */
        emailsTabs: serializedEmailsTabs,

        /**
         * Referência aos componentes dos novos e-mails que a pessoa está escrevendo.
         */
        newEmails: [],

        /**
         * Último recurso acessado pelas abas do cockpit para restaurar quando o usuário navegador.
         */
        restoreRoomPerCockpitTab: {},

        /**
         * Registros das salas que o operador atual precisa aceitar ou recusar.
         */
        convitesDeAtendimento: [],

        /**
         * Convites para entrar como participante em um atendimento.
         */
        invitesToJoinRoom: [],

        /**
         * Registros das salas que estão em fila.
         * Atendimentos em fila não foram atendidos por ninguém ainda e nem estão sendo mostrados como convite para alguém.
         */
        atendimentosEmFila: 0,
    },
    getters: {
        ...chatStore.getters,

        abaSelecionada: state => state.abaSelecionada,

        personalAssistantRoom: state => state.personalAssistantRoom,

        personalAssistantUnreadEvents: state => {
            return state.eventosNãoLidos.filter(não_lido => não_lido.mensageiro_canal == 'personal_assistant').length
        },

        /**
         * Abas da área de emails.
         */
        emailsTabs: state => state.emailsTabs,

        /**
         * ID da sala a ser restaurada baseada na última visita da aba.
         */
        restoreRoomForCockpitTab: state => tab => {
            return state.restoreRoomPerCockpitTab[tab]
        },

        /**
         * O cockpit está no modo pesquisa?
         *
         * No modo pesquisa, as salas mostradas no painel lateral são as salas encontradas pelo critério de busca, e não
         * baseado nos registros em memória sincronizados por websocket.
         */
        inSearchMode: state => {
            return state.appliedFilters.query != '' || state.appliedFilters.conditions.length > 0
        },

        /**
         * Ids das salas que estão selecionadas para executar uma ação em massa.
         */
        selectedRoomsForBulkAction: state => {
            return state.selectedRoomsForBulkAction
        },

        /**
         * A sala está selecionada para executar ação em massa?
         */
        isSelectedForBulkAction: state => roomId => {
            return state.selectedRoomsForBulkAction.find(room => room.id == roomId)
        },

        /**
         * Retorna o ID da sala de conversa selecionada com base na URL da página.
         */
        idSalaSelecionada: (state, getters, rootState) => {
            let match = rootState.urlPáginaAtual.match(/\/chat\/rooms\/(\d+)/)
            if (!match) return null
            return match[1]
        },

        /**
         * A sala selecionada possui um painel no menu da esquerda (navegação/index)?
         */
        selectedRoomHasLeftPanel: (state, getters) => {
            let roomId = getters.idSalaSelecionada
            if (roomId) {
                let room = getters.findRoomById(roomId)
                if (room) {
                    return roomTabName(room) == 'email'
                }
            }

            return false
        },

        convites: state => {
            return state.convitesDeAtendimento
        },

        invitesToJoinRoom: state => {
            return state.invitesToJoinRoom || []
        },

        atendimentosEmFila: state => {
            return state.atendimentosEmFila
        },

        /**
         * Encontra o registro da sala que está na lista de convites pendentes por id.
         */
        encontrarConvitePorId: state => id => {
            return state.convitesDeAtendimento.find(sala => sala.id == id)
        },

        /**
         * Filtros aplicado na inbox. Filtros view + filtros customizados.
         */
        helpdeskMergedFilters: state => {
            if (state.helpdeskFilter) {
                return { ...state.helpdeskFilter.configuração.fitros, ...state.helpdeskCustomFilters }
            }

            return state.helpdeskCustomFilters
        },

        /**
         * As notificações estão habiltiadas para a sala especificada? O controle das notificações é feito por usuário.
         */
        notificaçõesHabilitadas: (state, getters) => id_sala => {
            let sala = getters.findRoomById(id_sala)
            if (!sala) return null

            let configs = sala.atributos_extras.configurações
            if (!configs) return true

            let configDoUser = configs['core_users:' + meta('user-id')]
            if (!configDoUser) return true

            if (configDoUser['notificações'] === undefined) return true

            return !!configDoUser['notificações']
        },

        salasDoTeams: (state, getters) => {
            if (state.salasByTab['teams'] === null) return null
            return sortByDesc(state.salasByTab['teams'], s => getters.últimoEventoDaSala(s.id).created_at)
        },

        salasDoChat: (state, getters) => {
            return sortByDesc(state.salasByTab['chat'], s => getters.últimoEventoDaSala(s.id).created_at)
        },

        emailRooms: (state, getters) => {
            return sortByDesc(state.salasByTab['email'], s => getters.últimoEventoDaSala(s.id).created_at)
        },

        /**
         * Qtde. de salas com pelo menos um evento de mensagem não lida.
         */
        eventosNãoLidosDoTeams: (state, getters) => {
            return getters.unreadEventsLengthPerTab(
                não_lido => não_lido.mensageiro_canal == 'direct_message' || não_lido.mensageiro_canal == 'group'
            )
        },

        /**
         * Quantidade de eventos não lidos em salas do chat.
         */
        eventosNãoLidosDoChat: (state, getters) => {
            return getters.unreadEventsLengthPerTab(não_lido => canaisDoChat.indexOf(não_lido.mensageiro_canal) !== -1)
        },

        unreadEmailsEvents: (state, getters) => {
            return getters.unreadEventsLengthPerTab(não_lido => canaisDoEmail.indexOf(não_lido.mensageiro_canal) !== -1)
        },

        unreadEventsLengthPerTab: (state, getters) => predicate => {
            return getters.unreadEventsPerTab(predicate).length
        },

        unreadEventsPerTab: state => predicate => {
            let eventos = state.eventosNãoLidos.filter(predicate)

            let salas = {}
            eventos.forEach(evento => {
                salas[evento.evento.mensageiro_id] = true
            })

            return Object.keys(salas)
        },
    },
    mutations: {
        ...chatStore.mutations,

        /**
         * Reseta os dados do módulo chat para o estado inicial.
         * Essa mutation é usada somente em testes.
         */
        resetState(state) {
            Object.assign(state, defaultState)
        },

        /**
         * Atualiza o registro da sala de assistente pessoal.
         */
        updatePersonalAssistantRoom(state, room) {
            state.personalAssistantRoom = room
        },

        /**
         * Atualiza a aba selecionada no menu de navegação da inbox.
         * Pode ser 'teams', 'chat' ou 'email'.
         */
        atualizarAbaSelecionada(state, aba) {
            if (state.abaSelecionada != aba) {
                // Se houve alteração da aba selecionada na navegação do cockpit as salas da ação em massa
                // devem ser removidas.
                if (state.selectedRoomsForBulkAction.length > 0) {
                    state.selectedRoomsForBulkAction = []
                }
            }

            state.abaSelecionada = aba
        },

        /**
         * Mostra/esconde os filtros do helpdesk.
         */
        toggleHelpdeskFiltersVisible(state) {
            state.helpdeskFiltersVisible = !state.helpdeskFiltersVisible
        },

        /**
         * Mostraos filtros do helpdesk.
         */
        showHelpdeskFiltersVisible(state) {
            state.helpdeskFiltersVisible = true
        },

        /**
         * Esconde os filtros do helpdesk.
         */
        hideAndDiscardHelpdeskFiltersVisible(state) {
            if (state.helpdeskFiltersVisible) {
                state.helpdeskFiltersVisible = false
            } else {
                document.dispatchEvent(new CustomEvent('inbox:discard-filters'))
            }
        },

        /**
         * Atualiza o filtro selecionado no helpdesk.
         */
        updateHelpdeskFilter(state, filter) {
            if (state.helpdeskFilter != filter) {
                // Se o usuário trocou de visualização no helpdesk as salas que estavam selecionadas
                // para ação em massa devem ser limpas.
                state.selectedRoomsForBulkAction = []
            }

            state.helpdeskFilter = filter
        },

        /**
         * Atualiza os filtros personalizados da helpdesk que o usuário pode definir na página da Inbox.
         */
        updateHelpdeskCustomFilters(state, customFilters) {
            state.helpdeskCustomFilters = customFilters
        },

        /**
         * Atualiza os registros paginados do helpdesk.
         */
        updateHelpdeskPaginatedRooms(state, paginatedRooms) {
            state.helpdeskPaginatedRooms = paginatedRooms
        },

        /**
         * Atualiza o termo de pesquisa no cockpit.
         */
        updateAppliedFiltersQuery(state, query) {
            state.appliedFilters.query = query
        },

        /**
         * Adiciona um novo componente a lista de emails que estão sendo enviados pelo usuário atual.
         */
        startNewEmail(state) {
            state.newEmails.push(state.newEmails.length)
        },

        discardNewEmail(state, index) {
            state.newEmails = state.newEmails.filter(i => i != index)
        },

        /**
         * Limpa as salas que estão selecionadas para bulk action.
         */
        clearRoomsIdsForBulkAction(state) {
            state.selectedRoomsForBulkAction = []
        },

        /**
         * Adiciona/remove uma sala das ações em massa.
         */
        toggleRoomIdToBulkAction(state, room) {
            let exists = state.selectedRoomsForBulkAction.find(r => r.id == room.id)
            if (exists) {
                state.selectedRoomsForBulkAction = state.selectedRoomsForBulkAction.filter(r => r.id != room.id)
            } else {
                state.selectedRoomsForBulkAction.push(room)
            }
        },

        /**
         * Adiciona uma nova aba no canal de email.
         */
        addEmailsTab(state, tab) {
            let alreadyExists = state.emailsTabs.find(existingTab => existingTab.href == tab.href)
            if (!alreadyExists) {
                state.emailsTabs.push(tab)
                localStorage.serializedEmailsTabs = JSON.stringify(state.emailsTabs)
            }
        },

        /**
         * Atualiza o estado da aba do cockpit para restaurar quando o usuário clicar na aba de volta.
         */
        setRestoreRoomForCockpitTab(state, { tab, roomId }) {
            state.restoreRoomPerCockpitTab[tab] = roomId
        },

        /**
         * Remove uma aba do canal de emails.
         */
        removeEmailsTab(state, tabIndex) {
            state.emailsTabs.splice(tabIndex, 1)
            localStorage.serializedEmailsTabs = JSON.stringify(state.emailsTabs)
        },

        /**
         * Remove a aba no helpdesk baseado no ID da sala.
         */
        removeHelpdeskTabBasedOnRoomId(state, roomId) {
            state.emailsTabs = state.emailsTabs.filter(tab => {
                return tab.href != `/chat/rooms/${roomId}`
            })
            localStorage.serializedEmailsTabs = JSON.stringify(state.emailsTabs)
        },

        /**
         * Quando o usuário navega para uma sala diretamente, nós precisamos atualizar a aba do inbox que está selecionada.
         */
        atualizarAbaSelecionadaDeAcordoComASala(state, sala) {
            if (canaisDoTeams.indexOf(sala.canal_atual) !== -1) state.abaSelecionada = 'teams'
            if (canaisDoChat.indexOf(sala.canal_atual) !== -1) state.abaSelecionada = 'chat'
            if (canaisDoEmail.indexOf(sala.canal_atual) !== -1) state.abaSelecionada = 'email'
        },

        /**
         * Adiciona uma sala na lista de convites pendentes.
         */
        adicionarConviteDeAtendimento(state, sala) {
            state.convitesDeAtendimento.push(sala)
        },

        /**
         * Atualizar os convites para entrar no atendimento como participante.
         */
        updateInvitesToJoinRoom(state, invites) {
            state.invitesToJoinRoom = invites
        },

        /**
         * Adiciona o convite para se juntar ao atendimento como participante.
         */
        addInviteToJoinRoom(state, invite) {
            state.invitesToJoinRoom.push(invite)
        },

        atualizarAtendimentosEmFila(state, count) {
            state.atendimentosEmFila = count
        },

        /**
         * Incrementa a quantidade de atendimento em fila. Emitido quando um atendimento é iniciado.
         */
        incrementarAtendimentosEmFila(state) {
            state.atendimentosEmFila = state.atendimentosEmFila + 1
        },

        /**
         * Decrementa a quantidade de atendimentos em fila. Emitido quando um atendimento for distribuído para alguém.
         */
        decrementarAtendimentosEmFila(state) {
            state.atendimentosEmFila = Math.max(0, state.atendimentosEmFila - 1)
        },

        /**
         * Adiciona a sala a lista de salas que o usuário está vendo na interface. A primeira verificação que fazemos
         * na função é "já existe?". O motivo disso é que no caso de conversa direta, só deve existir uma única sala que
         * representa a conversa entre dois usuários. Se o usuário tentar iniciar uma nova conversa com uma pessoa mais de uma,
         * a sala que já existe é retornada.
         */
        adicionarSala(state, opts) {
            let { sala } = opts

            // Salas que estão no cache servem para alimentar as visualizações da inbox. Se uma mensagme for recebida enquanto
            // o usuário estiver em, por exemplo, "Respondidos recentementes", nós não podemos impedir que a sala seja adicionada
            // na tab email por duplicidade com o cache. Por isso `ignoreCache`.
            let existingRoom = findRoomById(state, sala.id, { ignoreCache: true })

            if (existingRoom) {
                if (!Object.isFrozen(existingRoom)) {
                    Object.keys(sala).forEach(key => {
                        Vue.set(existingRoom, key, sala[key])
                    })
                }

                return
            }

            if (!sala.último_evento) {
                sala = Object.freeze({
                    ...sala,
                    último_evento: {
                        created_at: formatarISO(utcParaLocal(sala.latest_event_sent_at || sala.created_at)),
                        corpo_texto: '',
                        atributos_extras: {
                            acks: {},
                        },
                    },
                })
            }

            let tabName = roomTabName(sala)

            // Caso especial para atendimentos da inbox.
            // Os atendimentos da inbox não estão armazenados em uma lista na memória, mas sim em uma lista
            // paginada de acordo com os filtros selecionados na lateral. É possível que essa sala passe
            // pelos filtros que estão selecioandos, o que significa que a sala deve ser adicionada dinamicamente na lista.
            if (tabName == 'email') {
                if (
                    state.helpdeskPaginatedRooms &&
                    state.helpdeskPaginatedRooms.data &&
                    state.helpdeskPaginatedRooms.filters
                ) {
                    if (
                        roomPassAgainstFilters(
                            sala,
                            state.helpdeskFilter.configuração.filtros,
                            state.helpdeskCustomFilters
                        )
                    ) {
                        state.helpdeskPaginatedRooms.data.unshift(sala)
                    }

                    let filters = cockpitHelpdeskFilters()
                    if (Array.isArray(filters)) {
                        filters.forEach(filter => {
                            if (
                                roomPassAgainstFilters(sala, filter.configuração.filtros, state.helpdeskCustomFilters)
                            ) {
                                filter.total += 1
                            }
                        })
                    }
                }

                return
            }

            let tab = state.salasByTab[tabName]

            if (tab) {
                if (tab.length >= 35 && !opts.grow) {
                    let sorted = sortBy(tab, room => {
                        let eventos = state.eventosPorSala[room.id]

                        if (eventos && eventos.length > 0) {
                            return eventos[eventos.length - 1].created_at
                        }

                        return sala.último_evento.created_at
                    })

                    let stack = 50
                    while (stack > 0 && state.salasByTab[tabName].length > 35) {
                        stack -= 1
                        let oldest = sorted.pop()

                        if (!oldest) break

                        state.salasByTab[tabName] = state.salasByTab[tabName].filter(s => s.id != oldest.id)
                    }
                }

                state.salasByTab[tabName].push(sala)
            } else {
                state.salasByTab[tabName] = [sala]
            }
        },

        initTeamsWithEmptyList(state) {
            if (!Array.isArray(state.salasByTab['teams'])) {
                state.salasByTab['teams'] = []
            }
        },

        /**
         * Atualiza o registro das configurações (silenciar, bloquear, etc.) da sala.
         */
        atualizarConfiguraçãoDaSala(state, { id_sala, configurações }) {
            let sala = findRoomById(state, id_sala)
            Vue.set(sala.atributos_extras, 'configurações', configurações)
        },

        /**
         * Atualiza o registro da sala que está sendo mostrado no convite de atendimento.
         */
        atualizarSalaNoConviteDeAtendimento(state, sala) {
            state.convitesDeAtendimento = state.convitesDeAtendimento.map(s => {
                if (s.id == sala.id) return sala
                return s
            })
        },

        /**
         * Remove a sala da lista de convites pendentes, geralmente chamado após o usuário
         * aceitar/recusar.
         */
        removerConviteDeAtendimento(state, sala) {
            state.convitesDeAtendimento = state.convitesDeAtendimento.filter(convite => convite.id != sala.id)
        },

        /**
         * Remove o convite para se juntar ao atendimento porque o usuário recusou.
         */
        removeInviteToJoinRoom(state, inviteId) {
            state.invitesToJoinRoom = state.invitesToJoinRoom.filter(invite => invite.invite.id != inviteId)
        },
    },
    actions: {
        ...chatStore.actions,

        async loadMoreRooms({ commit, state }, { tab, timestamp, ignore_ids }) {
            let { data } = await axios.get('/chat/private_api/rooms', { params: { tab, timestamp, ignore_ids } })

            // Se nós tentamos carregar mas nenhuma foi encontrada, quer dizer que houve um engano e nós
            // devemos esconder o carregamento dinâmico. Isso é um mecanismo de auto-correção.
            if (data.length === 0) {
                commit('updateTabTotal', { tab: tab, total: state.salasByTab[tab].length })
            }

            data.forEach(room => {
                commit('adicionarSala', { sala: room, grow: true })
            })
        },

        /**
         * Carrega o registro da sala de forma dinâmica. Registros de sala de conversa podem chegar até o usuário de diversas formas,
         * como carregadas no primeiro load (recarregarSalas), evento de distribuição (convite do chat), quando o usuário cria um novo grupo (chat/adicionarSalaENavegarParaEla).
         *
         * Porém em alguns casos, nós vamos receber uma mensagem antes de carregar a sala. Quando isso acontece, o registro da sala deve ser carregado.
         * Isso geralmente quer dizer uma das duas opções:
         *
         * 1. O usuário foi adicionado em um grupo depois de ter iniciado a sessão e alguém mandou uma mensagem no grupo.
         * 2. O usuário recebeu uma mensagem em um atendimento antigo que ainda não foi finalizado, mas está fora da interface e não foi carregado no primeiro load.
         */
        carregarSala({ commit, getters, dispatch }, { id_sala, emitir_alerta_após_carregar }) {
            axios
                .get('/chat/private_api/rooms/' + id_sala)
                .then(({ data: sala }) => {
                    if (sala.status == 'queued') {
                        // Se a sala está em fila ainda, nós devemos parar por aqui. O que aconteceu é que o contato enviou mensagens nessa sala, o que
                        // gerou alertas não lidos. Por padrão, nós tentamos carregar a sala de todo alerta não lido que não sabemos da sala, afinal a mensagem
                        // não lida pode ter sido enviada em uma conversa arquivada - e nós precisamos desarquivar.
                        return
                    }

                    commit('adicionarSala', { sala })

                    if (emitir_alerta_após_carregar) {
                        dispatch('talvezEmitirAlertasParaNovoEvento', { id_sala, ...emitir_alerta_após_carregar })
                    }

                    // Pode ser que essa sala tenha sido arquivada pelo usuário, e alguém mandou uma mensagem. Caso seja verdade,
                    // nós precisamos desarquivar a conversa.
                    dispatch('talvezDesarquivarSala', sala)
                })
                .catch(err => {
                    let eventos = getters.eventosNãoLidosDaSala(id_sala).map(registro_não_lido => {
                        commit('limparEventoNãoLido', registro_não_lido)
                        return true
                    })

                    if (eventos.length == 0) return

                    markRoomEventsAsRead(fastlane, { id: id_sala })
                })
        },

        async reloadTicket({ commit, dispatch, state }, room) {
            axios.get('/chat/private_api/rooms/' + room.id).then(({ data: sala }) => {
                commit('adicionarSala', { sala })
            })

            Vue.delete(state.eventosPorSala, room.id)
            dispatch('carregarEventosDaSala', { id_sala: room.id, room_token: room.token })
        },

        async reloadTicketMessages({ dispatch }, room) {
            dispatch('carregarEventosDaSala', { id_sala: room.id, room_token: room.token })
        },

        /**
         * Quando o operador aceita o convite de conversa, nós devemos adicionar a sala na Store e navegar para a página.
         */
        async adicionarSalaENavegarParaEla({ commit, dispatch, state }, { sala }) {
            commit('adicionarSala', { sala })

            visitarPágina('/chat/rooms/' + sala.id)

            dispatch('talvezDesarquivarSala', sala)

            let id_sala = sala.id
            if (!state.eventosPorSala[id_sala] || state.eventosPorSala[id_sala].length == 0) {
                dispatch('carregarEventosDaSala', { id_sala, room_token: sala.token })
            }
        },

        /**
         * Evento emitido quando o usuário selecionar uma sala. No menu de navegações, o link para abrir uma sala
         * é apenas uma transição de página. Quando a página for aberta, na callback `mounted` do componente Vue essa
         * função será chamada.
         */
        async selecionarSala({ dispatch, state, getters, commit }, id_sala) {
            let alreadyExists = state.roomsIdsCacheRotation.find(id => id == id_sala)
            if (!alreadyExists) {
                state.roomsIdsCacheRotation.push(id_sala)
                if (state.roomsIdsCacheRotation.length > MAX_ROOMS_CACHE_ROTATION) {
                    state.roomsIdsCacheRotation.shift()
                }
            }

            dispatch('talvezMarcarEventosDaSalaSelecionadaComoLidos')

            let sala = getters.findRoomById(id_sala)

            if (sala) {
                commit('atualizarAbaSelecionadaDeAcordoComASala', sala)
                commit('setRestoreRoomForCockpitTab', { tab: roomTabName(sala), roomId: sala.id })
            } else {
                console.log('não tinha o registro da sala.')
            }

            if (
                !state.eventosPorSala[id_sala] ||
                state.eventosPorSala[id_sala].length == 0 ||
                state.eventosPorSala[id_sala].loading
            ) {
                dispatch('carregarEventosDaSala', { id_sala, room_token: sala && sala.token })
            } else {
                document.dispatchEvent(new CustomEvent('chat:scroll-to-bottom', { detail: { id_sala, force: true } }))
            }
        },

        /**
         * Abre um atendimento em uma nova aba no canal de email.
         */
        async selectRoomWithNewEmailTab({ dispatch, commit, getters, state }, roomId) {
            dispatch('selecionarSala', roomId)
            let sala = getters.findRoomById(roomId)

            if (!sala) {
                let { data } = await axios.get('/chat/private_api/rooms/' + roomId)
                commit('pushRoomToTab', { tab: 'cache', room: data })

                commit('addEmailsTab', {
                    href: '/chat/rooms/' + roomId + '?view=' + state.helpdeskFilter.id,
                    icon: 'outline-mail',
                    title: nomeDaSala(data),
                    subtitle: data.protocolo,
                })

                return
            }

            commit('addEmailsTab', {
                href: '/chat/rooms/' + roomId + '?view=' + state.helpdeskFilter.id,
                icon: 'outline-mail',
                title: nomeDaSala(sala),
                subtitle: sala.protocolo,
            })
        },

        async search({ state, dispatch, rootState }, opts = {}) {
            state.appliedFilters.searching = true

            let currentTabChannels = []
            let defaultFilters = {
                keyword_users_ids: rootState.usuário.id,
                skip_default_space_id_filter: true,
            }

            if (state.abaSelecionada == 'teams') {
                currentTabChannels = canaisDoTeams
            } else if (state.abaSelecionada == 'email') {
                // Não precisamos rodar filtro no email. A área de emails possui uma lógica própria.
            } else if (state.abaSelecionada == 'chat') {
                currentTabChannels = canaisDoChat
            }

            try {
                let { data } = await axios.get('/chat/private_api/helpdesk_filters', {
                    params: {
                        ...defaultFilters,
                        page: opts.page || 1,
                        query: state.appliedFilters.query,
                        keyword_canal_atual: currentTabChannels,
                    },
                })

                state.appliedFilters.searching = false
                state.appliedFilters.result = data
            } catch (err) {
                state.appliedFilters.searching = false
                state.appliedFilters.result = null
            }
        },

        /**
         * Confirmação que o evento foi enviado e persistido com sucesso. O id_temporário irá existir se a sessão que enviou
         * a mensagem for a mesma que está aberta.
         */
        async confirmarEnvioDeMensagem({ commit, getters, state }, { evento, id_temporário }) {
            let eventoTemporárioNessaSessão = getters
                .eventosDaSala(evento.mensageiro_id)
                .find(e => e.id_temporário == id_temporário)

            if (eventoTemporárioNessaSessão) {
                commit('confirmarEnvioDeEvento', {
                    id_sala: evento.mensageiro_id,
                    evento: evento,
                    id_temporário,
                })
            } else {
                commit('mesclarEventosDaSala', { id_sala: evento.mensageiro_id, eventos: [evento] })
            }

            let ack = state.acksDeEventosNãoPersistidos.find(e => e.evento.id == evento.id)
            if (ack) {
                state.acksDeEventosNãoPersistidos = state.acksDeEventosNãoPersistidos.filter(a => a != ack)
                commit('atualizarAck', ack)
            }
        },

        /**
         * Atenção: existem dois eventos de confirmação de leitura: "confirmação_de_leitura" e "minha_confirmação_de_leitura".
         * Qual a diferença entre eles?
         *
         * "confirmação_de_leitura" -> Evento recebido pelo autor da mensagem quando outras pessoas lêem a mensagem.
         * "minha_confirmação_de_leitura" -> Evento recebido pela própria pessoa que leu o evento, inclusive em outras abas para sincronizar o estado da aplicação.
         */
        async atualizarMinhaConfirmaçãoDeLeitura({ commit }, { eventos }) {
            eventos.forEach(registro_não_lido => {
                commit('limparEventoNãoLido', registro_não_lido)
            })
        },

        /**
         * O usuário atual enviou uma mensagem para um canal externo que falhou. Nós precisamos avisar essa pessoa
         * que a falha ocorreu.
         */
        async processarFalhaNoEnvioDeMensagem({ commit }, { canal, evento, error }) {
            commit('atualizarFalhaDeEnvio', { evento, error })
        },

        async aceitarConvite({ commit, dispatch }, sala) {
            commit('removerConviteDeAtendimento', sala)
            await axios.post('/chat/private_api/invites/accept', { id_sala: sala.id })
        },

        async recusarConvite({ commit }, sala) {
            commit('removerConviteDeAtendimento', sala)
            await axios.post('/chat/private_api/invites/decline', { id_sala: sala.id })
        },

        async acceptInviteToJoinRoom({ commit, dispatch }, invite) {
            commit('removeInviteToJoinRoom', invite.id)
            let { data } = await axios.post('/chat/private_api/invites_to_join_room/accept?invite_id=' + invite.id)
        },

        async declineInviteToJoinRoom({ commit }, invite) {
            commit('removeInviteToJoinRoom', invite.id)
            await axios.post('/chat/private_api/invites_to_join_room/decline?invite_id=' + invite.id)
        },

        /**
         * Toggle dos alertas da sala. Desabilita caso esteja habilitado, habilita caso esteja desabilitado.
         * Alertas sonoros e notificações AppNotification.
         */
        async toggleNotificaçõesDaSala({ commit }, sala) {
            let chave = 'core_users:' + meta('user-id')

            if (!sala.atributos_extras) Vue.set(sala, 'atributos_extras', {})

            let configurações = sala.atributos_extras.configurações || {}
            let configDoUser = configurações[chave] || {}

            // As configurações do usuário começam vazias, e o alerta sonoro é habilitado por padrão.
            // Nesse caso, undefined significa que está habilitado.
            if (configDoUser['notificações'] === undefined) {
                Vue.set(configDoUser, 'notificações', false)
            } else {
                Vue.set(configDoUser, 'notificações', !configDoUser['notificações'])
            }

            Vue.set(configurações, chave, configDoUser)

            commit('atualizarConfiguraçãoDaSala', { id_sala: sala.id, configurações })

            await axios.put('/chat/private_api/rooms/settings/' + sala.id, configDoUser)

            let mensagem = configDoUser['notificações']
                ? __('Notificações habilitadas.')
                : __('Notificações desabilitadas.')

            toast('success', mensagem)
        },

        /**
         * Atualiza a flag "ocultado" nas configs do usuário e remove a sala da interface.
         */
        async arquivarSala({ commit }, sala) {
            await axios.post('/chat/private_api/rooms/archives', { room_id: sala.id })
            visitarPágina('/chat/rooms')
        },

        /**
         * É possível que o usuário tenha arquivado a sala (o que fez o registo sair da interface), e alguém do grupo mandou
         * uma nova mensagem, o que fez a sala voltar para a tela. Para garantir que a sala irá continuar na tela após um f5,
         * nós precisamos desarquivar.
         */
        async talvezDesarquivarSala({ commit }, sala) {
            let chave = 'core_users:' + meta('user-id')
            if (!sala.atributos_extras) Vue.set(sala, 'atributos_extras', {})
            let configurações = sala.atributos_extras.configurações || {}
            let configDoUser = configurações[chave]

            if (configDoUser && configDoUser['arquivado']) {
                await axios.delete('/chat/private_api/rooms/archives/' + sala.id)
                commit('atualizarConfiguraçãoDaSala', { id_sala: sala.id, configurações })
            }
        },

        async reopenRooms({ getters, dispatch }, rooms) {
            let roomsIds = rooms.map(r => r.id)

            let firstId = roomsIds.pop()

            let { data } = await axios.post('/chat/private_api/rooms/reopens', {
                room_id: firstId,
                other_rooms_ids: roomsIds,
            })

            if (data.refresh) {
                rooms.forEach(room => {
                    dispatch('reloadTicket', room)
                })
            }
        },

        async deleteRooms({ getters, dispatch }, roomsIds) {
            for (let roomId of roomsIds) {
                let room = getters.findRoomById(roomId)
                if (room) {
                    dispatch('markRoomEventsAsRead', room)
                    dispatch('deleteChatRoom', room)
                }
            }

            let firstId = roomsIds.pop()

            await axios.post('/chat/rooms/deletings', {
                room_id: firstId,
                other_rooms_ids: roomsIds,
            })

            document.dispatchEvent(new CustomEvent('chat:room-deletings-finished'))
        },

        async reportRoomsAsSpam({ getters, dispatch }, roomsIds) {
            for (let roomId of roomsIds) {
                let room = getters.findRoomById(roomId)
                if (room) {
                    dispatch('markRoomEventsAsRead', room)
                    dispatch('deleteChatRoom', room)
                }
            }

            let firstId = roomsIds.pop()

            await axios.post('/chat/rooms/spams', {
                room_id: firstId,
                other_rooms_ids: roomsIds,
            })
        },
    },
}

/**
 * Nome da sala mostrada na interface para o operador, na navegação e no topo da sala selecionada.
 *
 * @param {*} sala
 * @return {string}
 */
export function nomeDaSala(sala) {
    if (sala.atributos_extras.name || sala.atributos_extras.nome) {
        return sala.atributos_extras.name || sala.atributos_extras.nome
    }

    if (sala.canal_atual == 'direct_message') {
        let outroId = idDoOutroUserEmMensagemDireta(sala)
        return userProfile(outroId).name
    }

    if (sala.canal_atual == 'personal_assistant') {
        return __('Meu Teclia')
    }

    if (sala.atributos_extras.contacts_ids && sala.atributos_extras.contacts_ids.length > 0) {
        return contactProfile(sala.atributos_extras.contacts_ids[0]).name
    }

    if (sala.atributos_extras.is_broadcast) {
        return __('Lista de transmissão')
    }

    return 'Carregando...'
}

export function emailDaSala(sala) {
    if (sala.atributos_extras.contacts_ids && sala.atributos_extras.contacts_ids.length > 0) {
        return contactProfile(sala.atributos_extras.contacts_ids[0]).email
    }
}

export function hasPendingClassification(room, classificationFields) {
    let pending = pendingClassificationFields(room, classificationFields)
    if (pending > 0) return true

    let space = getCoreSpace(room.id_space)

    if (space.atributos_extras && space.atributos_extras.chat_classification_type == 'complete') {
        if (!room.status_id) return true

        if (room.atributos_extras.contacts_ids && room.atributos_extras.contacts_ids.length == 1) {
            let id = room.atributos_extras.contacts_ids[0]
            let profile = contactProfile(id)
            if (profile && !profile.qualification_id) return true
        }
    }

    return false
}

/**
 * Qtde. de campos na classificação que estão faltando para a sala.
 *
 * @param {*} room
 * @return {number}
 */
export function pendingClassificationFields(room, classificationFields) {
    let count = 0
    let roomData = { ...room, ...room.atributos_extras }

    for (let field of classificationFields) {
        let conditionals

        if (!Form.required(field)) {
            continue
        }

        if (field.reference) {
            conditionals = field.reference.atributos_extras.conditionals
        } else if (field.atributos_extras) {
            conditionals = field.atributos_extras.conditionals
        }

        if (conditionals && Array.isArray(conditionals) && conditionals.length > 0) {
            for (let condition of conditionals) {
                if (Form.conditionalMatch(roomData, condition)) {
                    if (!roomData[Form.name(field)]) {
                        count += 1
                    }
                }
            }
        } else {
            if (!roomData[Form.name(field)]) {
                count += 1
            }
        }
    }

    return count
}

/**
 * Badge mostrado caso a conversa seja entre pessoas de clientes diferentes.
 *
 * @param {*} sala
 * @return {string|null}
 */
export function badgeDoCliente(sala) {
    if (sala.canal_atual == 'direct_message') {
        let outroId = idDoOutroUserEmMensagemDireta(sala)
        return userProfile(outroId).cliente
    }

    return null
}

/**
 * URL do avatar mostrado na interface para o operador, na navegação e no topo da sala selecionada.
 *
 * @param {*} sala
 * @return {string}
 */
export function avatarDaSala(sala) {
    if (sala.canal_atual == 'direct_message') {
        return userProfile(idDoOutroUserEmMensagemDireta(sala)).url_avatar_32px
    }

    if (sala.canal_atual == 'group') {
        return sala.atributos_extras.url_avatar_128px || asset('/img/avatar_default_grupo.svg')
    }

    if (sala.canal_atual == 'personal_assistant') {
        return asset('/img/teclia_bot.png')
    }

    if (sala.atributos_extras.url_avatar_128px) {
        return sala.atributos_extras.url_avatar_128px
    }

    if (sala.atributos_extras.is_broadcast) {
        return asset('/img/avatar_default_broadcast.svg')
    }

    if (sala.atributos_extras.is_group) {
        return asset('/img/avatar_default_grupo.svg')
    }

    if (sala.atributos_extras.contacts_ids && sala.atributos_extras.contacts_ids.length > 0) {
        return contactProfile(sala.atributos_extras.contacts_ids[0]).url_avatar_32px
    }

    return 'http://localhost:8000/storage/1/2020/11/09/avatar_32px_core_users_1.png'
}

/**
 * ID do outro usuário que faz parte de uma sala mensagem-direta.
 *
 * @param {*} sala
 * @return {string}
 */
export function idDoOutroUserEmMensagemDireta(sala) {
    // Caso especial para a conversa direta entre você e si mesmo.
    let directMessage = sala.canal_atual == 'direct_message'
    let ids = sala.atributos_extras.users_ids
    let currentUserId = meta('user-id')
    if (directMessage && ids.length == 2 && ids[0] == currentUserId && ids[1] == currentUserId) {
        return currentUserId
    }

    return sala.atributos_extras.users_ids.find(id => id != meta('user-id'))
}

/**
 *
 * @param {string} canal
 * @return {string}
 */
export function nomeDoCanal(canal) {
    switch (canal) {
        case 'email':
            return __('E-mail')
        case 'web_form':
            return __('Formulário de contato')
        case 'web_messenger':
            return __('Web messenger')
        case 'whatsapp_qr_code':
            return __('Whatsapp QR Code')
        case 'whatsapp_twilio':
            return __('Whatsapp Business')
        case 'whatsapp_remote':
            return __('Whatsapp Remoto')
        case 'instagram':
            return __('Instagram')
        case 'telegram':
            return __('Telegram')
        case 'messenger':
            return __('Facebook messenger')
        case 'direct_message':
            return __('Mensagem direta')
        case 'group':
            return __('Grupo')
    }
}
